import type { View } from './store';

/**
 * WHAT EACH SCREEN IS CALLED — in ONE place.
 *
 * Every surface that names a screen reads from here: the page heading at the top
 * of the app, and the "← Back to X" trail after you drill from one screen into
 * another. They used to keep separate copies, and the copies drifted — the trail
 * had no entry for `approvals`, so drilling out of the sign-off screen offered
 * "← Back to approvals" while the nav, correctly, called that same screen
 * "Sign-offs". A screen named two different things in one app is exactly the
 * inconsistency this map exists to make impossible.
 *
 * It is a COMPLETE `Record<View, string>` on purpose: add a view and the build
 * fails until it is named here, so a new screen can never slip through nameless
 * and fall back to showing its raw internal id.
 */
export const VIEW_TITLES: Record<View, string> = {
  /* The personal, cross-board hub (#457) — everything assigned to the signed-in
     person, across every board, to act on. A lone top row, for everyone. */
  mywork: 'My Work',
  board: 'Board',
  plan: 'Plan',
  /* This project's money on one screen — a Plan child. */
  budgetpay: 'Budget & Payments',
  /* The Learning & Governance spine — 3.19. The project's own log and register,
     and their cross-board library in the Team group. */
  decisions: 'Decisions',
  /* The RAID register — a Plan child (0193). One screen, a tab per register. */
  riskregister: 'Risk Register',
  lessons: 'Lessons',
  lessonslibrary: 'Lessons Library',
  sprints: 'Sprints',
  backlog: 'Backlog',
  releases: 'Releases',
  documents: 'Documents',
  /* The work sign-off screen. Named "Sign-offs" everywhere a person can see it,
     never "Approvals" — that word is the admin's join inbox (`requests`). The
     internal view id stays `approvals` for routing; the NAME is this. */
  approvals: 'Sign-Offs',
  /* Legacy route: 'requests' now redirects into the Workspace hub → People &
     access → Requests, so it wears that screen's title rather than one of its own. */
  requests: 'Workspace',
  activity: 'Activity',
  dashboard: 'Insights',
  portfolio: 'Portfolio',
  /* The finance "Authorised to pay" queue — every board's approved-not-yet-paid
     invoices in one place, for whoever pays them. Nested under Portfolio (it reads
     across boards), and named for what it is rather than the generic "Payments",
     which a person read as this project's payments. */
  payments: 'Ready to Pay',
  /* Who authorises a payment, and above what value — the approval-chain builder. */
  payapproval: 'Payment Approval',
  settings: 'Settings',
  admin: 'Workspace',
  account: 'Your Account',
  /* The dedicated Help page (#501) — the full-screen home of the "how do I…"
     corpus. The page draws its own title and subline, so this name is for the
     nav and the "Back to …" trail. */
  help: 'Help',
};

/**
 * The few names that read better in the middle of a "Back to …" sentence than
 * they do as a heading — "Back to the Board", "Back to the backlog". Every other
 * view falls through to its plain title above, so the trail always has a real
 * screen name and can never show a raw view id.
 */
const WAY_BACK_PROSE: Partial<Record<View, string>> = {
  board: 'the Board',
  backlog: 'the backlog',
};

/** The label the "← Back to X" trail shows for the screen you came from. */
export function wayBackLabel(view: View): string {
  return WAY_BACK_PROSE[view] ?? VIEW_TITLES[view];
}
